import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CaseManagementImmediateResponseSurvey from "../pages/center-pov/CaseManagement/CaseManagementImmediateResponseSurvey";
import CaseManagementDamageAssessment from "../pages/center-pov/CaseManagement/CaseManagementDamageAssessment";
import CaseManagementAssistanceLog from "../pages/center-pov/CaseManagement/CaseManagementAssistanceLog";
import CaseManagementDA from "../pages/center-pov/CaseManagement/CaseManagementDA";
import CaseManagementLockbox from "../pages/center-pov/CaseManagement/CaseManagementLockbox";
import CaseWorkNotes from "../pages/center-pov/CaseManagement/CaseWorkNotes";
import {
  collection,
  query,
  where,
  doc,
  getDoc,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { useDisasterObjContext } from "../context/DisasterObjContext";
import { ReactComponent as FireIcon } from "../assets/disaster-icons/fire.svg";
import { ReactComponent as FloodIcon } from "../assets/disaster-icons/flood.svg";
import { ReactComponent as TornadoIcon } from "../assets/disaster-icons/tornado.svg";
import { ReactComponent as HurricaneIcon } from "../assets/disaster-icons/hurricane.svg";
import { ReactComponent as EarthquakeIcon } from "../assets/disaster-icons/earthquake.svg";
import { ReactComponent as VolcanoIcon } from "../assets/disaster-icons/volcano.svg";
import { useAuth } from "../components/AuthContext";

const ViewRecoveryTracker = () => {
  const { subtab } = useParams();
  const { disaster, setDisaster } = useDisasterObjContext();
  const navigate = useNavigate();
  const [caseStatus, setCaseStatus] = useState("Open");
  const [caseNumber, setCaseNumber] = useState(null);
  const [clientName, setClientName] = useState("");
  const [accessPermissions, setAccessPermissions] = useState({
    immediateResponse: false,
    damageAssessment: false,
    assistanceLog: false,
    lockbox: false,
  });

  const previouslySelectedDisasterId = sessionStorage.getItem(
    "accessed_disaster_id"
  );
  const selectedDisasters = sessionStorage.getItem("accessed_disaster_id");
  const [currentDisasterId, setCurrentDisasterId] = useState(
    previouslySelectedDisasterId || ""
  );
  const [disasterOptions, setDisasterOptions] = useState([]);
  const [activeDisasters, setActiveDisasters] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [orgDetails, setOrgDetails] = useState({});

  const dropdownRef = useRef(null);
  const disasterTypeStyles = {
    Fire: { color: "#780404", background: "#F00", icon: <FireIcon /> },
    Flood: { color: "#15659B", background: "#2788C9", icon: <FloodIcon /> },
    Tornado: { color: "#454545", background: "#454545", icon: <TornadoIcon /> },
    Hurricane: {
      color: "#6B0E93",
      background: "#6B0E93",
      icon: <HurricaneIcon />,
    },
    Earthquake: {
      color: "#F56D13",
      background: "#D29000",
      icon: <EarthquakeIcon />,
    },
    Volcano: { color: "#945E14", background: "#945E14", icon: <VolcanoIcon /> },
  };

  const { role } = useAuth();

  // Extract fetchCaseData function so it can be called from dropdown handler
  const fetchCaseData = async () => {
    try {
      const accessedUserId = sessionStorage.getItem("accessedUserId");
      const accessedDisasterId = sessionStorage.getItem("accessed_disaster_id");
      const loggedInUserId = sessionStorage.getItem("userId"); // Get logged in user's ID

      if (!loggedInUserId) {
        console.log("No logged in user found");
        return;
      }

      // Fetch citizen information to get their name
      if (accessedUserId) {
        const citizenDoc = await getDoc(doc(db, "users", accessedUserId));
        if (citizenDoc.exists()) {
          const citizenData = citizenDoc.data();
          const firstName = citizenData.first_name || "";
          const lastName = citizenData.last_name || "";
          setClientName(`${firstName} ${lastName}`.trim());
          console.log("Set client name to:", `${firstName} ${lastName}`);
        } else {
          console.log("Citizen document not found");
          setClientName("Unknown Citizen");
        }
      }

      // First get the logged in user's data to find their partner org
      const userDoc = await getDoc(doc(db, "users", loggedInUserId));
      if (!userDoc.exists()) {
        console.log("Logged in user document not found");
        return;
      }

      let orgId = null;
      let orgDoc = null;
      if (role == "partner") {
        const userData = userDoc.data();
        orgId = userData.partner_id; // Get partner org ID from user data
        orgDoc = await getDoc(doc(db, "partners", orgId));
        setOrgDetails({
          id: orgId,
          center_id: userData.center_id,
          ...orgDoc.data(),
        });
      } else if (role == "center") {
        const userData = userDoc.data();
        orgId = userData.center_id; // Get center org ID from user data
        orgDoc = await getDoc(doc(db, "centers", orgId));
        setOrgDetails({ id: orgId, ...orgDoc.data() });
      }

      if (!orgId) {
        console.log("No partner organization found for user");
        return;
      }

      // Now get partner permissions
      if (orgDoc.exists()) {
        const orgData = orgDoc.data();

        // Extract view_only_access_to settings
        const viewOnlyAccess = orgData.view_only_access_to || {};
        // console.log("View only access settings:", viewOnlyAccess);

        // Set access permissions based on partner settings
        setAccessPermissions({
          immediateResponse: viewOnlyAccess.immediate_resp,
          damageAssessment: viewOnlyAccess.dmg_assessment,
          assistanceLog: viewOnlyAccess.assistance_log,
          lockbox: viewOnlyAccess.lockbox,
        });
      }

      // Continue with case management query...
      if (!accessedUserId || !accessedDisasterId) {
        console.log("Missing accessed user or disaster ID");
        return;
      }

      // Use the correct collection name 'case_management' (with underscore)
      const caseManagementRef = collection(db, "case_management");
      const q = query(
        caseManagementRef,
        where("citizen_id", "==", accessedUserId),
        where("disaster_id", "==", accessedDisasterId)
      );

      const caseManagementSnapshot = await getDocs(q);

      if (!caseManagementSnapshot.empty) {
        const caseData = caseManagementSnapshot.docs[0].data();
        setCaseNumber(caseData.case_number);
        setCaseStatus(caseData.status);
      } else {
        // Check survey responses for case number
        try {
          // Try to get the survey_responses collection
          const surveyResponsesCollRef = collection(
            db,
            "users",
            accessedUserId,
            "survey_responses"
          );
          const surveyResponsesQuery = query(surveyResponsesCollRef);
          const surveyResponsesSnapshot = await getDocs(surveyResponsesQuery);

          if (!surveyResponsesSnapshot.empty) {
            const surveyData = surveyResponsesSnapshot.docs[0].data();

            // Check if case_number exists at the top level
            if (surveyData.case_number) {
              setCaseNumber(surveyData.case_number);
              setCaseStatus(surveyData.status || "Pending");
            }
            // Check if disasters array exists and contains the current disaster
            else if (
              surveyData.disasters &&
              Array.isArray(surveyData.disasters)
            ) {
              // Find the disaster entry that matches the current disaster ID
              const disasterEntry = surveyData.disasters.find(
                (disaster) => disaster.disaster_id === accessedDisasterId
              );

              if (disasterEntry && disasterEntry.case_number) {
                setCaseNumber(disasterEntry.case_number);
                setCaseStatus(disasterEntry.status || "Pending");
              }
            }
          }
        } catch (error) {
          console.error("Error fetching survey responses:", error);
        }
      }
    } catch (error) {
      console.error("Error in fetchCaseData:", error);
    }
  };

  useEffect(() => {
    fetchCaseData();
  }, []);

  useEffect(() => {
    const fetchDisasters = async () => {
      try {
        const accessedUserId = sessionStorage.getItem("accessedUserId");
        const accessedDisasterId = sessionStorage.getItem(
          "accessed_disaster_id"
        );

        if (!accessedUserId || !accessedDisasterId) return;

        // Get the citizen's document to check their county
        const citizenDoc = await getDoc(doc(db, "users", accessedUserId));
        if (!citizenDoc.exists()) {
          console.error("Citizen document not found");
          return;
        }

        const citizenData = citizenDoc.data();
        const citizenCounty = citizenData.county;

        if (!citizenCounty) {
          console.error("Citizen county not found");
          return;
        }

        // Query disasters based on the citizen's county
        const disastersRef = collection(db, "declared-disaster");
        const q = query(
          disastersRef,
          where("affected_counties", "array-contains", citizenCounty),
          where("is_deleted", "==", false)
        );

        const querySnapshot = await getDocs(q);
        const disasters = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort disasters by status
        const activeDisasters = disasters.filter(
          (d) => d.disaster_status === "active"
        );
        const inactiveDisasters = disasters.filter(
          (d) => d.disaster_status === "inactive"
        );
        const closedDisasters = disasters.filter(
          (d) => d.disaster_status === "closed"
        );
        const sortedDisasters = [
          ...activeDisasters,
          ...inactiveDisasters,
          ...closedDisasters,
        ];

        setDisasterOptions(sortedDisasters);
        setActiveDisasters(activeDisasters.map((d) => d.id));

        // Find and set the previously selected disaster
        const selectedDisaster = sortedDisasters.find(
          (d) => d.id === accessedDisasterId
        );
        if (selectedDisaster) {
          setCurrentDisasterId(selectedDisaster.id);
          setDisaster(selectedDisaster);
        }
      } catch (error) {
        console.error("Error fetching disasters:", error);
      }
    };

    fetchDisasters();
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Add disaster change handler
  const handleDisasterChange = useCallback(
    (selectedDisaster) => {
      if (!selectedDisaster) return;

      // Only update if the disaster has actually changed
      if (
        sessionStorage.getItem("accessed_disaster_id") !== selectedDisaster.id
      ) {
        sessionStorage.setItem("accessed_disaster_id", selectedDisaster.id);
        setDisaster(selectedDisaster);

        // Refetch case data with new disaster
        fetchCaseData();
      }
    },
    [setDisaster, fetchCaseData]
  );

  const tabs = [
    { id: "irs", label: "Immediate Response Survey" },
    { id: "damageassessment", label: "Damage Assessment" },
    { id: "assistancelog", label: "Assistance Log" },
    { id: "lockbox", label: "Lockbox" },
    { id: "casework", label: "Case Work Notes" },
  ];

  const handleTabClick = (tabId) => {
    navigate(`/viewrecovery/${tabId}`);
  };

  const handleGoBack = () => {
    if (role === "partner") {
      navigate("/citizensearch");
    } else if (role == "center") {
      navigate("/database/citizensearch");
    }
  };

  const tabComponent = {
    irs: activeDisasters ? (
      <CaseManagementImmediateResponseSurvey
        selectedDisasters={selectedDisasters}
        activeDisasters={activeDisasters}
        parentSetCaseNumber={setCaseNumber}
        caseStatus={caseStatus}
        accessPermissions={accessPermissions}
      />
    ) : (
      <div className="flex items-center justify-center p-8">
        <p>Loading...</p>
      </div>
    ),
    damageassessment: (
      <CaseManagementDA
        caseStatus={caseStatus}
        accessPermissions={accessPermissions}
      />
    ),
    assistancelog: (
      <CaseManagementAssistanceLog
        selectedDisasters={selectedDisasters}
        activeDisasters={activeDisasters}
        caseStatus={caseStatus}
        accessPermissions={accessPermissions}
      />
    ),
    casework: <CaseWorkNotes orgDetails={orgDetails} />,
    lockbox: (
      <CaseManagementLockbox
        caseStatus={caseStatus}
        accessPermissions={accessPermissions}
        onNavigateBack={() => handleTabClick("assistancelog")}
      />
    ),
  };

  return (
    <div className="flex-1">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="px-6 py-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleGoBack}
                className="flex items-center gap-2 rounded-md bg-[#f0f5ff] px-4 py-2 text-[#003460] transition-all hover:bg-[#e0ebff] hover:text-[#002340]"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                <span className="font-medium">Back to Citizen Search</span>
              </button>
              {/* Disaster Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <select
                  value={currentDisasterId}
                  onChange={(e) => {
                    const selectedDisaster = disasterOptions.find(
                      (d) => d.id === e.target.value
                    );
                    if (selectedDisaster) {
                      handleDisasterChange(selectedDisaster);
                      setCurrentDisasterId(selectedDisaster.id);
                    }
                  }}
                  className="hover:bg-gray-50 disabled:bg-gray-100 flex appearance-none items-center gap-2 rounded-md border bg-white px-4 py-2 pr-10 shadow-sm disabled:cursor-not-allowed"
                >
                  <option value="" disabled>
                    Select a disaster...
                  </option>
                  {disasterOptions.map((disaster) => (
                    <option key={disaster.id} value={disaster.id}>
                      {disaster.disaster_name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="text-right">
              <h2 className="text-xl font-semibold text-[#003460]">
                {clientName}
              </h2>
              {caseNumber && (
                <p className="text-gray-600 font-medium">
                  Case #: {caseNumber}
                </p>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-4 border-b">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`-mb-px px-4 py-2 ${
                  subtab === tab.id
                    ? "border-b-2 border-blue-500 font-medium text-blue-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {!currentDisasterId ? (
          <div className="flex h-64 flex-col items-center justify-center text-gray-500">
            <div className="text-lg mb-2 font-semibold">
              No disaster selected
            </div>
            <div>
              Please select a disaster from the dropdown above to continue.
            </div>
          </div>
        ) : !activeDisasters ? (
          <div className="flex h-64 flex-col items-center justify-center">
            <div className="text-lg text-gray-500">Loading...</div>
          </div>
        ) : (
          tabComponent[subtab] || <div>Select a valid tab</div>
        )}
      </div>
    </div>
  );
};

export default ViewRecoveryTracker;
